import React, { useState, useEffect, useRef } from 'react';
import { Key, Plus, Trash2, Copy, Check, Eye, EyeOff, RefreshCw, ChevronDown, AlertTriangle, Shield, Clock, Activity, BookOpen, ChevronUp } from 'lucide-react';

const BASE_URL = 'https://app.bedee.cloud';
import { toast } from 'sonner';
import * as api from '../../api';

function CopyBlock({ code }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="relative group">
      <div className="rounded-lg overflow-x-auto" style={{ background: '#0F172A' }}>
        <pre className="p-3 text-xs font-mono" style={{ color: '#7DD3FC', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{code}</pre>
      </div>
      <button
        onClick={() => { navigator.clipboard.writeText(code); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
        className="absolute top-2 right-2 p-1.5 rounded opacity-0 group-hover:opacity-100 transition-all"
        style={{ background: '#1E293B', color: copied ? '#4ADE80' : '#94A3B8' }}>
        {copied ? <Check size={12} /> : <Copy size={12} />}
      </button>
    </div>
  );
}

function ApiDoc() {
  const [open, setOpen] = useState(false);

  return (
    <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
      {/* En-tête cliquable */}
      <button
        onClick={() => setOpen(o => !o)}
        data-testid="api-doc-toggle"
        className="w-full flex items-center justify-between px-5 py-4 text-left transition-all hover:opacity-80"
        style={{ background: 'var(--bg-subtle)' }}>
        <div className="flex items-center gap-3">
          <BookOpen size={16} style={{ color: 'var(--primary-light)' }} />
          <div>
            <p className="text-sm font-semibold" style={{ color: 'var(--text)' }}>Documentation de l'API</p>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Référence complète pour développeurs — endpoints, paramètres, exemples</p>
          </div>
        </div>
        {open ? <ChevronUp size={16} style={{ color: 'var(--text-muted)' }} /> : <ChevronDown size={16} style={{ color: 'var(--text-muted)' }} />}
      </button>

      {open && (
        <div className="px-5 py-5 space-y-7 border-t" style={{ borderColor: 'var(--border)' }}>

          {/* URL de base */}
          <section>
            <h4 className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: 'var(--text-muted)' }}>URL de base</h4>
            <CopyBlock code={`${BASE_URL}/api/public/`} />
            <p className="text-xs mt-2" style={{ color: 'var(--text-muted)' }}>
              Tous les endpoints publics sont préfixés par cette URL. La documentation complète est également disponible via API (sans authentification) :
            </p>
            <CopyBlock code={`curl ${BASE_URL}/api/public/docs`} />
          </section>

          {/* Authentification */}
          <section>
            <h4 className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: 'var(--text-muted)' }}>Authentification</h4>
            <p className="text-xs mb-2" style={{ color: 'var(--text-secondary)' }}>
              Chaque requête doit inclure un header <code className="px-1 py-0.5 rounded font-mono" style={{ background: '#F1F5F9' }}>Authorization</code> avec votre clé API :
            </p>
            <CopyBlock code={`Authorization: Bearer bde_xxxxxxxxxxxxxxxxxxxxxxxx`} />
            <p className="text-xs mt-2" style={{ color: 'var(--text-muted)' }}>
              Les clés sont générées depuis cette page. La valeur brute n'est affichée qu'une seule fois à la création.
            </p>
          </section>

          {/* Endpoints */}
          <section>
            <h4 className="text-xs font-bold uppercase tracking-wider mb-4" style={{ color: 'var(--text-muted)' }}>Endpoints disponibles</h4>

            {/* Entreprises */}
            <div className="mb-5 space-y-3">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold px-2 py-0.5 rounded font-mono" style={{ background: '#DCFCE7', color: '#15803D' }}>GET</span>
                <code className="text-sm font-mono font-semibold" style={{ color: 'var(--text)' }}>/api/public/entreprises</code>
                <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: '#FAF5FF', color: '#7C3AED' }}>entreprises:nom</span>
              </div>
              <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Retourne la liste des entreprises de votre organisation.</p>

              <div className="rounded-lg overflow-hidden border" style={{ borderColor: 'var(--border)' }}>
                <table className="w-full text-xs">
                  <thead>
                    <tr style={{ background: 'var(--bg-subtle)' }}>
                      <th className="px-3 py-2 text-left font-semibold" style={{ color: 'var(--text-muted)' }}>Paramètre</th>
                      <th className="px-3 py-2 text-left font-semibold" style={{ color: 'var(--text-muted)' }}>Type</th>
                      <th className="px-3 py-2 text-left font-semibold" style={{ color: 'var(--text-muted)' }}>Défaut</th>
                      <th className="px-3 py-2 text-left font-semibold" style={{ color: 'var(--text-muted)' }}>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ['page',  'integer', '1',    'Numéro de page'],
                      ['limit', 'integer', '100',  'Résultats par page (max 1000)'],
                      ['since', 'ISO 8601','—',    'Ne retourner que les entrées modifiées après cette date'],
                    ].map(([p, t, d, desc]) => (
                      <tr key={p} className="border-t" style={{ borderColor: 'var(--border-light)' }}>
                        <td className="px-3 py-2"><code className="font-mono" style={{ color: '#7C3AED' }}>{p}</code></td>
                        <td className="px-3 py-2" style={{ color: 'var(--text-muted)' }}>{t}</td>
                        <td className="px-3 py-2"><code className="font-mono" style={{ color: '#64748B' }}>{d}</code></td>
                        <td className="px-3 py-2" style={{ color: 'var(--text-secondary)' }}>{desc}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <CopyBlock code={`curl -H "Authorization: Bearer bde_xxx" \\
  "${BASE_URL}/api/public/entreprises?page=1&limit=100"`} />

              <div>
                <p className="text-xs font-semibold mb-1" style={{ color: 'var(--text-muted)' }}>Réponse :</p>
                <CopyBlock code={`{
  "data": [
    { "id": "ENT-abc123", "nom": "Acme Corp", "updated_at": "2026-01-15T10:32:00+00:00" }
  ],
  "pagination": { "page": 1, "limit": 100, "total": 12788, "pages": 128, "has_more": true },
  "sync": { "since": null, "count": 1 }
}`} />
              </div>
            </div>

            <div style={{ height: 1, background: 'var(--border-light)' }} />

            {/* Contacts */}
            <div className="mt-5 space-y-3">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold px-2 py-0.5 rounded font-mono" style={{ background: '#DCFCE7', color: '#15803D' }}>GET</span>
                <code className="text-sm font-mono font-semibold" style={{ color: 'var(--text)' }}>/api/public/contacts</code>
                <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: '#EFF6FF', color: '#1D4ED8' }}>contacts:nom_prenom</span>
              </div>
              <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Retourne la liste des contacts de votre organisation. Mêmes paramètres que <code className="font-mono">/entreprises</code>. Inclut <code className="font-mono">entreprise_id</code> et <code className="font-mono">entreprise_ids</code> pour le lien contact ↔ entreprise.</p>

              <CopyBlock code={`curl -H "Authorization: Bearer bde_xxx" \\
  "${BASE_URL}/api/public/contacts?page=1&limit=100"`} />

              <div>
                <p className="text-xs font-semibold mb-1" style={{ color: 'var(--text-muted)' }}>Réponse :</p>
                <CopyBlock code={`{
  "data": [
    { "id": "PER-def456", "nom": "Dupont", "prenom": "Marie",
      "entreprise_id": "ENT-abc123",
      "entreprise_ids": ["ENT-abc123", "ENT-xyz789"],
      "updated_at": "2026-01-15T10:32:00+00:00" }
  ],
  "pagination": { "page": 1, "limit": 100, "total": 11589, "pages": 116, "has_more": true },
  "sync": { "since": null, "count": 1 }
}`} />
              </div>
            </div>
          </section>

          {/* Sync incrémentale */}
          <section>
            <h4 className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: 'var(--text-muted)' }}>Stratégie de sync incrémentale recommandée</h4>
            <div className="space-y-2">
              {[
                ['1', 'Sync initiale',       'Appelez sans `since`, parcourez toutes les pages. Sauvegardez localement `updated_at` de la dernière entrée reçue.'],
                ['2', 'Syncs suivantes',      'Appelez avec `?since=<dernière_date>`. Récupérez uniquement les nouvelles modifications.'],
                ['3', 'Pagination',           'Tant que `has_more: true`, incrémentez `page`. La liste est triée par `updated_at` ASC.'],
              ].map(([n, titre, desc]) => (
                <div key={n} className="flex items-start gap-3 p-3 rounded-lg" style={{ background: 'var(--bg-subtle)' }}>
                  <span className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ background: 'var(--primary-light)' }}>{n}</span>
                  <div>
                    <p className="text-xs font-semibold" style={{ color: 'var(--text)' }}>{titre}</p>
                    <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <CopyBlock code={`# Exemple de sync incrémentale
curl -H "Authorization: Bearer bde_xxx" \\
  "${BASE_URL}/api/public/entreprises?since=2026-01-15T10:00:00Z&page=1&limit=500"`} />
          </section>

          {/* Codes d'erreur */}
          <section>
            <h4 className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: 'var(--text-muted)' }}>Codes de réponse</h4>
            <div className="rounded-lg overflow-hidden border" style={{ borderColor: 'var(--border)' }}>
              <table className="w-full text-xs">
                <tbody>
                  {[
                    ['200', '#DCFCE7', '#15803D', 'Succès'],
                    ['400', '#FEF9C3', '#854D0E', 'Paramètre invalide (ex: format `since` incorrect)'],
                    ['401', '#FEF2F2', '#991B1B', 'Clé API manquante ou invalide'],
                    ['403', '#FEF2F2', '#991B1B', 'Clé désactivée ou scope insuffisant'],
                  ].map(([code, bg, color, msg]) => (
                    <tr key={code} className="border-b last:border-0" style={{ borderColor: 'var(--border-light)' }}>
                      <td className="px-3 py-2 w-16">
                        <span className="font-bold font-mono text-xs px-2 py-0.5 rounded" style={{ background: bg, color }}>{code}</span>
                      </td>
                      <td className="px-3 py-2" style={{ color: 'var(--text-secondary)' }}>{msg}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

        </div>
      )}
    </div>
  );
}

// ─── Scopes metadata ─────────────────────────────────────────────────────────
const SCOPE_META = {
  'entreprises:nom':      { label: 'Entreprises → Nom',          color: '#7C3AED', bg: '#FAF5FF', endpoint: 'GET /api/public/entreprises' },
  'contacts:nom_prenom':  { label: 'Contacts → Nom + Prénom + Email', color: '#1D4ED8', bg: '#EFF6FF', endpoint: 'GET /api/public/contacts'    },
};

function ScopeBadge({ scope }) {
  const m = SCOPE_META[scope];
  if (!m) return <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: '#F1F5F9', color: '#64748B' }}>{scope}</span>;
  return (
    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium"
      style={{ background: m.bg, color: m.color }}>
      {m.label}
    </span>
  );
}

// ─── Modale création ─────────────────────────────────────────────────────────
function CreateKeyModal({ scopes, onClose, onCreate }) {
  const [name, setName] = useState('');
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);

  const toggle = (key) =>
    setSelected(s => s.includes(key) ? s.filter(k => k !== key) : [...s, key]);

  const handleCreate = async () => {
    if (!name.trim()) { toast.error('Donnez un nom à la clé'); return; }
    if (!selected.length) { toast.error('Sélectionnez au moins un scope'); return; }
    setLoading(true);
    try {
      await onCreate(name.trim(), selected);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.4)' }}>
      <div className="rounded-2xl shadow-2xl w-full max-w-md mx-4" style={{ background: 'white' }}>
        <div className="px-6 pt-6 pb-4 border-b" style={{ borderColor: 'var(--border)' }}>
          <h3 className="text-base font-bold" style={{ color: 'var(--text)' }}>Créer une clé API</h3>
          <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>La clé complète sera affichée une seule fois après la création.</p>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Nom */}
          <div>
            <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--text-muted)' }}>Nom de la clé *</label>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Ex : Application mobile, CRM externe…"
              data-testid="api-key-name-input"
              className="w-full px-3 py-2.5 rounded-lg border text-sm outline-none focus:ring-2"
              style={{ borderColor: 'var(--border)', color: 'var(--text)', background: 'white' }}
              onKeyDown={e => e.key === 'Enter' && handleCreate()}
            />
          </div>

          {/* Scopes */}
          <div>
            <label className="block text-xs font-semibold mb-2" style={{ color: 'var(--text-muted)' }}>Données accessibles *</label>
            <div className="space-y-2">
              {scopes.map(s => {
                const m = SCOPE_META[s.key];
                const active = selected.includes(s.key);
                return (
                  <label key={s.key}
                    data-testid={`scope-${s.key}`}
                    className="flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all"
                    style={{
                      borderColor: active ? (m?.color || 'var(--primary-light)') : 'var(--border)',
                      background: active ? (m?.bg || 'var(--primary-50)') : 'white',
                    }}>
                    <div className="mt-0.5 flex-shrink-0 w-4 h-4 rounded border-2 flex items-center justify-center"
                      style={{ borderColor: active ? (m?.color || 'var(--primary-light)') : '#CBD5E1', background: active ? (m?.color || 'var(--primary-light)') : 'white' }}>
                      {active && <Check size={10} color="white" strokeWidth={3} />}
                    </div>
                    <input type="checkbox" className="sr-only" checked={active} onChange={() => toggle(s.key)} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold" style={{ color: active ? (m?.color || 'var(--primary-light)') : 'var(--text)' }}>{s.label}</p>
                      <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{s.description}</p>
                      <code className="text-xs mt-1 inline-block px-1.5 py-0.5 rounded font-mono" style={{ background: '#F1F5F9', color: '#64748B' }}>{s.endpoint}</code>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>
        </div>

        <div className="px-6 pb-6 flex gap-3 justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-sm font-semibold"
            style={{ background: 'var(--bg-secondary)', color: 'var(--text-muted)' }}>
            Annuler
          </button>
          <button onClick={handleCreate} disabled={loading} data-testid="create-api-key-btn"
            className="px-4 py-2 rounded-xl text-sm font-semibold text-white disabled:opacity-50"
            style={{ background: 'var(--primary-gradient)' }}>
            {loading ? 'Création…' : 'Créer la clé'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Modale affichage clé ─────────────────────────────────────────────────────
function ShowKeyModal({ keyData, onClose }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(keyData.key);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="rounded-2xl shadow-2xl w-full max-w-lg mx-4" style={{ background: 'white' }}>
        <div className="px-6 pt-6 pb-4 border-b" style={{ borderColor: 'var(--border)' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: '#F0FDF4' }}>
              <Key size={18} style={{ color: '#16A34A' }} />
            </div>
            <div>
              <h3 className="text-base font-bold" style={{ color: 'var(--text)' }}>Clé créée : {keyData.name}</h3>
              <p className="text-xs" style={{ color: '#16A34A' }}>Copiez-la maintenant — elle ne sera plus visible.</p>
            </div>
          </div>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div className="rounded-xl p-4 flex items-center gap-3"
            style={{ background: '#F8FAFC', border: '1px solid var(--border)' }}>
            <code className="flex-1 text-sm font-mono break-all select-all" style={{ color: 'var(--text)' }}>
              {keyData.key}
            </code>
            <button onClick={handleCopy} data-testid="copy-api-key-btn"
              className="flex-shrink-0 p-2 rounded-lg transition-all"
              style={{ background: copied ? '#F0FDF4' : '#F1F5F9', color: copied ? '#16A34A' : '#64748B' }}>
              {copied ? <Check size={16} /> : <Copy size={16} />}
            </button>
          </div>

          <div className="rounded-xl p-3 flex items-start gap-2" style={{ background: '#FFFBEB', border: '1px solid #FDE68A' }}>
            <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" style={{ color: '#D97706' }} />
            <p className="text-xs" style={{ color: '#92400E' }}>
              Cette clé sera stockée de façon sécurisée (hachée). Vous ne pourrez plus la consulter après fermeture de cette fenêtre.
            </p>
          </div>

          <div className="space-y-1.5">
            <p className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>Exemple d'utilisation</p>
            <div className="rounded-lg p-3 overflow-x-auto" style={{ background: '#1E293B' }}>
              <code className="text-xs font-mono whitespace-pre" style={{ color: '#7DD3FC' }}>
{`curl -H "Authorization: Bearer ${keyData.key}" \\
  "${window.location.origin}/api/public/entreprises?page=1&limit=100"`}
              </code>
            </div>
          </div>
        </div>

        <div className="px-6 pb-6 flex justify-end">
          <button onClick={onClose} data-testid="close-key-modal-btn"
            className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white"
            style={{ background: 'var(--primary-gradient)' }}>
            J'ai copié ma clé
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Composant principal ──────────────────────────────────────────────────────
export default function ApiKeys() {
  const [keys, setKeys] = useState([]);
  const [scopes, setScopes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newKeyData, setNewKeyData] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [keysRes, scopesRes] = await Promise.all([
        api.getApiKeys(),
        api.getApiScopes(),
      ]);
      setKeys(keysRes.data?.data || []);
      setScopes(scopesRes.data?.data || []);
    } catch {
      toast.error('Erreur chargement des clés API');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async (name, selectedScopes) => {
    try {
      const res = await api.createApiKey({ name, scopes: selectedScopes });
      const created = res.data?.data;
      setShowCreate(false);
      setNewKeyData(created);
      load();
      toast.success('Clé API créée');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur création');
    }
  };

  const handleDelete = async (key) => {
    if (!window.confirm(`Supprimer la clé "${key.name}" ? Les applications qui l'utilisent ne pourront plus se connecter.`)) return;
    try {
      await api.deleteApiKey(key.api_key_id);
      toast.success('Clé supprimée');
      load();
    } catch {
      toast.error('Erreur suppression');
    }
  };

  const handleToggle = async (key) => {
    try {
      await api.toggleApiKey(key.api_key_id);
      toast.success(key.is_active ? 'Clé désactivée' : 'Clé activée');
      load();
    } catch {
      toast.error('Erreur');
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold" style={{ color: 'var(--text)',  }}>Clés API</h2>
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>
            Donnez accès à vos données à des applications tierces via des clés en lecture seule.
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          data-testid="new-api-key-btn"
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white"
          style={{ background: 'var(--primary-gradient)' }}>
          <Plus size={15} /> Créer une clé
        </button>
      </div>

      {/* Documentation dépliable */}
      <ApiDoc />

      {/* Tableau des clés */}
      <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
        <table className="w-full" style={{ fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border)' }}>
              {['Nom', 'Préfixe', 'Scopes', 'Créée le', 'Dernière utilisation', 'Statut', ''].map(h => (
                <th key={h} className="px-4 py-3 text-left text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="text-center py-12 text-sm" style={{ color: 'var(--text-placeholder)' }}>Chargement…</td></tr>
            )}
            {!loading && keys.length === 0 && (
              <tr>
                <td colSpan={7} className="py-12 text-center">
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: 'var(--bg-secondary)' }}>
                      <Key size={20} style={{ color: 'var(--text-muted)' }} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>Aucune clé API</p>
                      <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>Créez votre première clé pour donner accès à vos données.</p>
                    </div>
                    <button onClick={() => setShowCreate(true)}
                      className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold"
                      style={{ background: 'var(--primary-50)', color: 'var(--primary-light)', border: '1px solid var(--primary-light)' }}>
                      <Plus size={12} /> Créer une clé
                    </button>
                  </div>
                </td>
              </tr>
            )}
            {!loading && keys.map(k => (
              <tr key={k.api_key_id} className="border-b last:border-0"
                style={{ borderColor: 'var(--border-light)', background: k.is_active ? 'white' : '#FAFAFA' }}>
                <td className="px-4 py-3">
                  <span className="font-semibold text-sm" style={{ color: k.is_active ? 'var(--text)' : 'var(--text-muted)' }}>
                    {k.name}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <code className="text-xs px-2 py-1 rounded font-mono" style={{ background: '#F1F5F9', color: '#475569' }}>
                    {k.key_prefix}
                  </code>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {(k.scopes || []).map(s => <ScopeBadge key={s} scope={s} />)}
                  </div>
                </td>
                <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-muted)' }}>
                  {formatDate(k.created_at)}
                </td>
                <td className="px-4 py-3">
                  {k.last_used_at ? (
                    <span className="flex items-center gap-1 text-xs" style={{ color: '#16A34A' }}>
                      <Activity size={11} /> {formatDate(k.last_used_at)}
                    </span>
                  ) : (
                    <span className="text-xs" style={{ color: 'var(--text-placeholder)' }}>Jamais utilisée</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => handleToggle(k)}
                    data-testid={`toggle-key-${k.api_key_id}`}
                    className="text-xs px-2.5 py-1 rounded-full font-semibold transition-all"
                    style={{
                      background: k.is_active ? '#F0FDF4' : '#FEF2F2',
                      color: k.is_active ? '#16A34A' : '#DC2626',
                      border: `1px solid ${k.is_active ? '#BBF7D0' : '#FECACA'}`,
                    }}>
                    {k.is_active ? 'Active' : 'Inactive'}
                  </button>
                </td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => handleDelete(k)}
                    data-testid={`delete-key-${k.api_key_id}`}
                    className="p-1.5 rounded-lg hover:bg-red-50 transition-all"
                    style={{ color: '#EF4444' }}>
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modales */}
      {showCreate && (
        <CreateKeyModal
          scopes={scopes}
          onClose={() => setShowCreate(false)}
          onCreate={handleCreate}
        />
      )}
      {newKeyData && (
        <ShowKeyModal
          keyData={newKeyData}
          onClose={() => setNewKeyData(null)}
        />
      )}
    </div>
  );
}
